"use client";

import Image from "next/image";
import Logo from "@/app/logo.svg";
import { useAuth } from '@/hooks/use-auth';
import { 
  Building2, 
  FolderTree, 
  Trash2, 
  Users, 
  ShieldCheck, 
  Settings,
  LogOut,
  ChevronDown,
  Share2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import { useEffect, useState } from 'react';
import { dmsApi } from '@/lib/api';

interface SidebarNavProps {
  onSelectFolder: (id: string) => void;
  activeView: 'all' | 'trash' | 'shared' | 'users' | 'roles' | 'settings';
  onSelectView: (view: SidebarNavProps['activeView']) => void;
}

export function SidebarNav({ onSelectFolder, activeView, onSelectView }: SidebarNavProps) {
  const { user, logout, isSuperAdmin, hasPermission } = useAuth();
  const [departments, setDepartments] = useState<any[]>([]);
  const [activeDept, setActiveDept] = useState<any>(null);

  useEffect(() => {
    const fetchDepts = async () => {
      try {
        const depts = await dmsApi.departments.list();
        setDepartments(depts);
        
        if (user?.departmentId) {
          const myDept = depts.find(d => d._id === user.departmentId);
          if (myDept) handleSelectDept(myDept);
        } else if (depts.length > 0) {
          handleSelectDept(depts[0]);
        }
      } catch (err) {
        console.error("Failed to fetch departments", err);
      }
    };
    fetchDepts();
  }, [user]);

  const handleSelectDept = async (dept: any) => {
    setActiveDept(dept);
    try {
      const root = await dmsApi.departments.getRootFolder(dept._id);
      onSelectFolder(root._id);
    } catch (err) {
      console.error("Failed to get root folder", err);
    }
  };

  const handleSelectAllFiles = async () => {
    if (activeDept?._id) {
      await handleSelectDept(activeDept);
    }
    onSelectView('all');
  };

  return (
    <div className="w-72 h-screen flex flex-col bg-white border-r border-border">
      <div className="p-6 flex items-center gap-3">
        <Image src={Logo} alt="CoopXL Docs" className="h-8 w-auto" priority />
      </div>

      <div className="flex-1 overflow-auto px-4 py-2 space-y-6">
        <div className="space-y-2">
          <p className="px-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Department</p>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="w-full justify-between h-11 px-3 bg-secondary/30 border-none shadow-none text-left">
                <div className="flex items-center gap-2 truncate">
                  <Building2 size={16} className="text-primary" />
                  <span className="truncate font-medium">{activeDept?.name || "Select Dept"}</span>
                </div>
                <ChevronDown size={14} className="text-muted-foreground" />
              </Button>
            </DropdownMenuTrigger>
            {isSuperAdmin && (
              <DropdownMenuContent className="w-[calc(18rem-2rem)]">
                {departments.map((dept) => (
                  <DropdownMenuItem key={dept._id} onClick={() => handleSelectDept(dept)}>
                    {dept.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            )}
          </DropdownMenu>
        </div>

        <div className="space-y-1">
          <p className="px-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Documents</p>
          <NavItem 
            icon={<FolderTree size={18} />} 
            label="All Files" 
            active={activeView === 'all'} 
            onClick={handleSelectAllFiles}
          />
          <NavItem 
            icon={<Trash2 size={18} />} 
            label="Trash" 
            active={activeView === 'trash'} 
            onClick={() => onSelectView('trash')}
          />
          <NavItem 
            icon={<Share2 size={18} />} 
            label="Shared with me" 
            active={activeView === 'shared'} 
            onClick={() => onSelectView('shared')}
          />
        </div>

        {(isSuperAdmin || hasPermission('view_user')) && (
          <div className="space-y-1">
            <p className="px-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Admin</p>
            <NavItem 
              icon={<Users size={18} />} 
              label="Users" 
              active={activeView === 'users'} 
              onClick={() => onSelectView('users')}
            />
            <NavItem 
              icon={<ShieldCheck size={18} />} 
              label="Roles" 
              active={activeView === 'roles'} 
              onClick={() => onSelectView('roles')}
            />
            <NavItem 
              icon={<Settings size={18} />} 
              label="Settings" 
              active={activeView === 'settings'} 
              onClick={() => onSelectView('settings')}
            />
          </div>
        )}
      </div>

      <div className="p-4 border-t">
        <div className="flex items-center gap-3 p-2 rounded-xl bg-secondary/20 mb-2">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold">
            {user?.name?.[0] || "U"}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold truncate">{user?.name || "User"}</p>
            <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
          </div>
        </div>
        <Button variant="ghost" className="w-full justify-start text-destructive hover:bg-destructive/10" onClick={logout}>
          <LogOut size={16} className="mr-2" />
          Log out
        </Button>
      </div>
    </div>
  );
}

function NavItem({ icon, label, active = false, onClick }: { icon: React.ReactNode, label: string, active?: boolean, onClick?: () => void }) {
  return (
    <Button 
      variant="ghost" 
      onClick={onClick}
      className={cn(
        "w-full justify-start gap-3 h-10 font-medium transition-all",
        active ? "bg-primary text-primary-foreground hover:bg-primary/90 shadow-md shadow-primary/20" : "text-muted-foreground hover:bg-secondary"
      )}
    >
      {icon}
      {label}
    </Button>
  );
}
